import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs';
import { TutorialVideoService } from '../../services/tutorial-video.service';

type TutorialPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export interface TutorialVideoStep {
  screenId: string;
  title?: string;
  introLabel?: string;
  recommendedScreenIds?: string[];
}

@Component({
  selector: 'app-tutorial-video-widget',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './tutorial-video-widget.component.html',
  styleUrl: './tutorial-video-widget.component.css'
})
export class TutorialVideoWidgetComponent implements OnInit {
  private hasRetriedTutorialVideo = false;
  private activeStepIndex = 0;

  @Input({ required: true }) screenId = '';
  @Input({ required: true }) title = '';
  @Input() introLabel = 'In-app training';
  @Input() teaserTitle = 'New here?';
  @Input() teaserDescription = 'Watch a quick walkthrough of this screen.';
  @Input() teaserButtonLabel = 'Play walkthrough';
  @Input() launcherLabel = 'Take the Tour';
  @Input() storageKey = '';
  @Input() teaserPosition: TutorialPosition = 'bottom-right';
  @Input() launcherPosition: TutorialPosition = 'bottom-right';
  @Input() allowLauncherDismiss = false;
  @Input() autoPlaySequence: TutorialVideoStep[] = [];

  showTutorialBanner = true;
  showTutorialLauncher = true;
  isTutorialPanelOpen = false;
  isTutorialLoading = false;
  isPreparingNextStep = false;
  tutorialVideoUrl = '';
  tutorialContentType = 'video/mp4';
  tutorialVideoError = '';

  @ViewChild('tutorialVideoPlayer') tutorialVideoPlayer?: ElementRef<HTMLVideoElement>;

  constructor(private readonly tutorialVideoService: TutorialVideoService) {}

  ngOnInit(): void {
    this.restoreTutorialPreference();
  }

  ngOnDestroy(): void {
    this.clearNextStepTimeout();
  }

  get modalAriaLabel(): string {
    return this.activeTitle || 'Tutorial walkthrough';
  }

  get reopenAriaLabel(): string {
    return `Take the ${this.activeTitle || 'tutorial'} tour`;
  }

  get teaserPositionClass(): string {
    return `tutorial-teaser--${this.teaserPosition}`;
  }

  get launcherPositionClass(): string {
    return `tutorial-reopen-btn--${this.launcherPosition}`;
  }

  openTutorialPanel(): void {
    const shouldRefreshVideo = !this.isTutorialPanelOpen;

    this.isTutorialPanelOpen = true;
    this.hideTutorialBanner();
    this.hasRetriedTutorialVideo = false;
    this.isPreparingNextStep = false;
    this.clearNextStepTimeout();

    if (shouldRefreshVideo) {
      this.activeStepIndex = this.initialStepIndex;
    }

    if (shouldRefreshVideo && !this.isTutorialLoading) {
      this.loadTutorialVideo(true);
    }
  }

  closeTutorialPanel(): void {
    this.pauseTutorialVideo();
    this.isPreparingNextStep = false;
    this.clearNextStepTimeout();
    this.isTutorialPanelOpen = false;
  }

  closeTutorialPanelOnBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeTutorialPanel();
    }
  }

  dismissTutorialBanner(): void {
    this.hideTutorialBanner();
  }

  dismissTutorialLauncher(event: MouseEvent): void {
    event.stopPropagation();
    this.showTutorialLauncher = false;
  }

  onTutorialVideoPlay(): void {
    this.hideTutorialBanner();
  }

  onTutorialVideoPlaybackError(): void {
    this.pauseTutorialVideo();

    if (!this.hasRetriedTutorialVideo && !this.isTutorialLoading) {
      this.hasRetriedTutorialVideo = true;
      this.loadTutorialVideo(true);
      return;
    }

    this.tutorialVideoError = 'The tutorial video file could not be played.';
  }

  onTutorialVideoEnded(): void {
    if (!this.hasNextStep) {
      return;
    }

    this.pauseTutorialVideo();
    this.isPreparingNextStep = true;
    this.tutorialVideoUrl = '';
    this.tutorialVideoError = '';
  }

  playNextTutorialVideo(screenId?: string): void {
    const recommendations = this.nextStepRecommendations;
    const nextStep = screenId
      ? recommendations.find(step => step.screenId === screenId)
      : recommendations[0];
    if (!nextStep || this.isTutorialLoading) {
      return;
    }

    this.activeStepIndex = this.tutorialSteps.findIndex(step => step.screenId === nextStep.screenId);
    this.hasRetriedTutorialVideo = false;
    this.isPreparingNextStep = false;
    this.loadTutorialVideo(true);
  }

  get activeTitle(): string {
    return this.activeStep.title || this.title;
  }

  get activeIntroLabel(): string {
    return this.activeStep.introLabel || this.introLabel;
  }

  get nextStepTitle(): string {
    return this.nextStepRecommendations[0]?.title || '';
  }

  get nextStepRecommendations(): TutorialVideoStep[] {
    const remainingSteps = this.tutorialSteps.slice(this.activeStepIndex + 1);
    const recommendedIds = this.activeStep.recommendedScreenIds;
    const recommendations = recommendedIds?.length
      ? remainingSteps.filter(step => recommendedIds.includes(step.screenId))
      : [];

    // Without explicit choices, retain the existing single-next-video flow.
    return recommendations.length ? recommendations : remainingSteps.slice(0, 1);
  }

  private get tutorialSteps(): TutorialVideoStep[] {
    if (this.autoPlaySequence.length > 0) {
      return this.autoPlaySequence;
    }

    return [{
      screenId: this.screenId,
      title: this.title,
      introLabel: this.introLabel
    }];
  }

  private get activeStep(): TutorialVideoStep {
    return this.tutorialSteps[this.activeStepIndex] || this.tutorialSteps[0];
  }

  private get initialStepIndex(): number {
    const matchedIndex = this.tutorialSteps.findIndex((step) => step.screenId === this.screenId);

    return matchedIndex >= 0 ? matchedIndex : 0;
  }

  private get hasNextStep(): boolean {
    return this.nextStepRecommendations.length > 0;
  }

  private loadTutorialVideo(forceRefresh = false): void {
    if (forceRefresh) {
      this.tutorialVideoUrl = '';
    }

    this.isTutorialLoading = true;
    this.tutorialVideoError = '';

    this.tutorialVideoService.getTutorialVideoUrl(this.activeStep.screenId).pipe(
      finalize(() => {
        this.isTutorialLoading = false;
      })
    ).subscribe({
      next: (resource) => {
        if (!resource.signedUrl) {
          this.tutorialVideoError = 'Tutorial video is not available right now.';
          return;
        }

        this.tutorialVideoUrl = resource.signedUrl;
        this.tutorialContentType = resource.contentType || 'video/mp4';
      },
      error: (error) => {
        console.trace('Error loading tutorial video', error);
        this.tutorialVideoError = 'Unable to load the tutorial video. Please try again.';
      }
    });
  }

  private restoreTutorialPreference(): void {
    this.showTutorialBanner = !this.hasStoredTutorialPreference();
  }

  private hideTutorialBanner(): void {
    this.showTutorialBanner = false;
    this.storeTutorialPreference();
  }

  private hasStoredTutorialPreference(): boolean {
    return localStorage.getItem(this.preferenceKey) === 'true';
  }

  private storeTutorialPreference(): void {
    localStorage.setItem(this.preferenceKey, 'true');
  }

  private get preferenceKey(): string {
    return this.storageKey || `${this.screenId}-tutorial-seen`;
  }

  private pauseTutorialVideo(): void {
    if (this.tutorialVideoPlayer?.nativeElement) {
      this.tutorialVideoPlayer.nativeElement.pause();
    }
  }

  private clearNextStepTimeout(): void {
    return;
  }
}